// src/components/shared/CookieConsent.tsx
import {
  BarChart3,
  Check,
  Cookie,
  Settings,
  Shield,
  Wrench,
  X,
} from "lucide-react";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

interface CookiePreferences {
  necessary: boolean;
  analytics: boolean;
  functional: boolean;
}

interface CookieConsentProps {
  onPreferencesSet?: (preferences: CookiePreferences) => void;
}

export default function CookieConsent({
  onPreferencesSet,
}: CookieConsentProps) {
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [preferences, setPreferences] = useState<CookiePreferences>({
    necessary: true, // Always required
    analytics: false,
    functional: false,
  });

  useEffect(() => {
    // Check if user has already set preferences
    const savedPreferences = localStorage.getItem("cookie-preferences");
    if (!savedPreferences) {
      // Show banner after a short delay for better UX
      setTimeout(() => setShowBanner(true), 1000);
    } else {
      const parsed = JSON.parse(savedPreferences);
      setPreferences(parsed);
      onPreferencesSet?.(parsed);
      setupTracking(parsed);
    }
  }, [onPreferencesSet]);

  const handleAcceptAll = () => {
    const allAccepted: CookiePreferences = {
      necessary: true,
      analytics: true,
      functional: true,
    };
    savePreferences(allAccepted);
  };

  const handleRejectOptional = () => {
    const minimal: CookiePreferences = {
      necessary: true,
      analytics: false,
      functional: false,
    };
    savePreferences(minimal);
  };

  const handleCustomPreferences = () => {
    savePreferences(preferences);
  };

  const savePreferences = (prefs: CookiePreferences) => {
    localStorage.setItem("cookie-preferences", JSON.stringify(prefs));
    setPreferences(prefs);
    setShowBanner(false);
    setShowSettings(false);
    onPreferencesSet?.(prefs);
    setupTracking(prefs);
  };

  const setupTracking = (prefs: CookiePreferences) => {
    // Google Analytics
    if (prefs.analytics && typeof window !== "undefined") {
      // Set up Google Analytics here when you have GA_MEASUREMENT_ID
      console.log("Analytics cookies enabled");

      // Example GA4 setup (uncomment when you have your GA ID):
      // const script = document.createElement('script');
      // script.src = 'https://www.googletagmanager.com/gtag/js?id=GA_MEASUREMENT_ID';
      // script.async = true;
      // document.head.appendChild(script);

      // (window as any).dataLayer = (window as any).dataLayer || [];
      // function gtag(...args: any[]) {
      //   (window as any).dataLayer.push(arguments);
      // }
      // (window as any).gtag = gtag;
      // gtag('js', new Date());
      // gtag('config', 'GA_MEASUREMENT_ID');
    }

    // Functional cookies (form persistence, UI preferences, etc.)
    if (prefs.functional) {
      console.log("Functional cookies enabled");
    }
  };

  const togglePreference = (type: keyof CookiePreferences) => {
    if (type === "necessary") return; // Can't disable necessary cookies

    setPreferences((prev) => ({
      ...prev,
      [type]: !prev[type],
    }));
  };

  if (!showBanner) return null;

  return (
    <>
      {/* Cookie Banner */}
      <div className="fixed bottom-0 left-0 right-0 z-50 bg-slate-900/95 backdrop-blur-sm border-t border-slate-600/50 shadow-2xl">
        <div className="max-w-7xl mx-auto p-4 lg:p-6">
          <div className="flex flex-col lg:flex-row items-start lg:items-center gap-4">
            <div className="flex items-start gap-3 flex-1">
              <Cookie className="w-6 h-6 text-orange-400 flex-shrink-0 mt-1" />

              <div className="flex-1 min-w-0">
                <h3 className="text-lg font-semibold text-white mb-2">
                  We use cookies to enhance your experience
                </h3>
                <p className="text-sm text-gray-300 mb-4 max-w-3xl leading-relaxed">
                  Essential cookies keep the site working. Optional cookies help
                  us analyse usage and remember your preferences. You can
                  customise your choices anytime.{" "}
                  <Link
                    to="/cookie-policy"
                    className="text-blue-400 hover:text-blue-300 underline"
                  >
                    Learn more
                  </Link>
                </p>

                <div className="flex flex-wrap gap-3">
                  <button
                    onClick={handleAcceptAll}
                    className="px-4 py-2 bg-gradient-to-r from-green-600 to-green-500 hover:from-green-700 hover:to-green-600 text-white text-sm font-semibold rounded-lg transition-all transform hover:scale-105 shadow-lg"
                  >
                    Accept All
                  </button>

                  <button
                    onClick={handleRejectOptional}
                    className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white text-sm font-semibold rounded-lg transition-colors border border-slate-600"
                  >
                    Essential Only
                  </button>

                  <button
                    onClick={() => setShowSettings(true)}
                    className="px-4 py-2 bg-slate-600 hover:bg-slate-500 text-white text-sm font-semibold rounded-lg transition-colors flex items-center gap-2 border border-slate-500"
                  >
                    <Settings className="w-4 h-4" />
                    Customise
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Cookie Settings Modal */}
      {showSettings && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm p-4 overflow-y-auto">
          <div className="max-w-2xl mx-auto my-8 bg-slate-800 rounded-xl border border-slate-600/50 shadow-2xl">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-slate-600/50">
              <div className="flex items-center gap-3">
                <Settings className="w-6 h-6 text-orange-400" />
                <h2 className="text-xl font-semibold text-white">
                  Cookie Preferences
                </h2>
              </div>
              <button
                onClick={() => setShowSettings(false)}
                className="p-1 hover:bg-slate-700 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-gray-400" />
              </button>
            </div>

            {/* Cookie Categories */}
            <div className="p-6 space-y-6">
              {/* Essential Cookies */}
              <div className="flex items-start justify-between p-4 bg-slate-700/30 rounded-lg border border-green-500/20">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <Shield className="w-4 h-4 text-green-400" />
                    <h3 className="font-semibold text-green-400">
                      Essential Cookies
                    </h3>
                    <span className="text-xs bg-green-500/20 text-green-300 px-2 py-1 rounded">
                      Required
                    </span>
                  </div>
                  <p className="text-sm text-gray-300 mb-2">
                    Keep the site secure and functional. These are always active
                    and cannot be disabled.
                  </p>
                  <p className="text-xs text-gray-400">
                    Session management, security, Culture Lab progress
                  </p>
                </div>
                <div className="ml-4 flex-shrink-0">
                  <div className="w-12 h-6 bg-green-500 rounded-full flex items-center justify-end px-1">
                    <div className="w-4 h-4 bg-white rounded-full"></div>
                  </div>
                </div>
              </div>

              {/* Analytics Cookies */}
              <div className="flex items-start justify-between p-4 bg-slate-700/30 rounded-lg border border-blue-500/20">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <BarChart3 className="w-4 h-4 text-blue-400" />
                    <h3 className="font-semibold text-white">
                      Analytics Cookies
                    </h3>
                  </div>
                  <p className="text-sm text-gray-300 mb-2">
                    Help us understand how you use our site so we can improve
                    the experience.
                  </p>
                  <p className="text-xs text-gray-400">
                    Google Analytics, usage patterns, performance metrics
                  </p>
                </div>
                <div className="ml-4 flex-shrink-0">
                  <button
                    onClick={() => togglePreference("analytics")}
                    className={`w-12 h-6 rounded-full flex items-center px-1 transition-colors ${
                      preferences.analytics
                        ? "bg-blue-500 justify-end"
                        : "bg-slate-600 justify-start"
                    }`}
                  >
                    <div className="w-4 h-4 bg-white rounded-full"></div>
                  </button>
                </div>
              </div>

              {/* Functional Cookies */}
              <div className="flex items-start justify-between p-4 bg-slate-700/30 rounded-lg border border-purple-500/20">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <Wrench className="w-4 h-4 text-purple-400" />
                    <h3 className="font-semibold text-white">
                      Functional Cookies
                    </h3>
                  </div>
                  <p className="text-sm text-gray-300 mb-2">
                    Remember your preferences and enable enhanced features for a
                    better experience.
                  </p>
                  <p className="text-xs text-gray-400">
                    Form auto-fill, UI preferences, personalisation
                  </p>
                </div>
                <div className="ml-4 flex-shrink-0">
                  <button
                    onClick={() => togglePreference("functional")}
                    className={`w-12 h-6 rounded-full flex items-center px-1 transition-colors ${
                      preferences.functional
                        ? "bg-purple-500 justify-end"
                        : "bg-slate-600 justify-start"
                    }`}
                  >
                    <div className="w-4 h-4 bg-white rounded-full"></div>
                  </button>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-between p-6 border-t border-slate-600/50">
              <Link
                to="/cookie-policy"
                className="text-blue-400 hover:text-blue-300 text-sm transition-colors"
              >
                View full Cookie Policy
              </Link>

              <div className="flex gap-3">
                <button
                  onClick={() => setShowSettings(false)}
                  className="px-4 py-2 text-gray-400 hover:text-white transition-colors"
                >
                  Cancel
                </button>

                <button
                  onClick={handleCustomPreferences}
                  className="px-6 py-2 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-semibold rounded-lg transition-all transform hover:scale-105 flex items-center gap-2"
                >
                  <Check className="w-4 h-4" />
                  Save Preferences
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
